import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2, X, RefreshCw, ExternalLink, Wallet, Copy, UserCheck, Clock, XCircle, Trash2, KeyRound, Film, BarChart3, Megaphone, ChevronDown, FileText, Zap, RotateCcw, Fingerprint } from "lucide-react";
import ProspectsPanel from "@/components/ProspectsPanel";
import MembersPanel from "@/components/MembersPanel";
import JourneyMap from "@/components/JourneyMap";
import api from "@/lib/api";
import { etStamp, etTime } from "@/lib/dates";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import Header from "@/components/Header";
import TrainingVideosModal from "@/components/TrainingVideosModal";
import VideoEngagement from "@/components/VideoEngagement";
import OriginChip from "@/components/OriginChip";

/** Wallet name behind a lead: always filled in, correctable on hover. A faint
 *  dot means it was worked out rather than proven by the link they used. */
const TIMELINE_LABELS = [
  ["created_at", "First seen"],
  ["sizzle_started_at", "Sizzle started"],
  ["sizzle_completed_at", "Sizzle finished"],
  ["present_started_at", "Presentation started"],
  ["present_completed_at", "Presentation finished"],
  ["info_submitted_at", "Details submitted"],
  ["onboarding_started_at", "Onboarding started"],
  ["updated_at", "Last activity"],
];

const SeenCell = ({ lead }) => {
  const tl = lead.timeline || {};
  const detail = TIMELINE_LABELS
    .filter(([k]) => tl[k])
    .map(([k, label]) => `${label}: ${etStamp(tl[k])} ET`)
    .join("\n");
  const last = tl.updated_at && tl.updated_at !== tl.created_at ? tl.updated_at : null;
  return (
    <span className="font-mono text-[11px] leading-tight block" title={detail} data-testid={`discover-seen-${lead.id}`}>
      <span className="text-ink-2">{etStamp(lead.created_at)}</span>
      {last && <span className="block text-ink-3">last {etTime(last)}</span>}
    </span>
  );
};

const WalletCell = ({ lead, onSet }) => {
  const [editing, setEditing] = useState(false);
  const choices = lead.sponsor_wallets || [];
  const why = lead.sponsor_wallet_source === "set"
    ? `Set by ${lead.sponsor_wallet_set_by || "an admin"} — manual picks are never overwritten`
    : lead.sponsor_wallet_source_label || "";

  if (!lead.sponsor_wallet_name) {
    return <span className="text-ink-3" data-testid={`discover-slug-none-${lead.id}`}>direct</span>;
  }
  if (editing) {
    return (
      <select
        autoFocus
        value={lead.sponsor_wallet_name}
        onBlur={() => setEditing(false)}
        onChange={(e) => { onSet(lead, e.target.value); setEditing(false); }}
        data-testid={`discover-wallet-pick-${lead.id}`}
        className="text-[11px] bg-white border border-black/15 rounded-md px-1.5 py-0.5 max-w-[160px]"
      >
        {choices.map((w) => <option key={w} value={w}>{w}</option>)}
      </select>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap">
      <span title={why} data-testid={`discover-wallet-name-${lead.id}`}
        className={lead.sponsor_wallet_source === "set" ? "font-medium" : ""}>
        {lead.sponsor_wallet_name}
      </span>
      {!lead.sponsor_wallet_proven && (
        <span title={why} data-testid={`discover-wallet-assumed-${lead.id}`}
          className="inline-block w-[5px] h-[5px] rounded-full bg-amber-400/90 shrink-0" />
      )}
      {choices.length > 1 && (
        <button type="button" onClick={() => setEditing(true)} title="Correct this wallet"
          data-testid={`discover-wallet-edit-${lead.id}`}
          className="opacity-0 group-hover:opacity-100 transition-opacity text-ink-3 hover:text-ink-1">
          <ChevronDown size={12} />
        </button>
      )}
    </span>
  );
};

/**
 * One page for every trainee — whether they went through the retired 12-lesson
 * Academy or the new CaryPact Onboarding V2. Rows are normalized so admins
 * never think about which flow a person used. The screen is organised by what
 * the admin needs to DO: Needs you → Prospects → Members → Inactive, plus
 * Insights (sponsors + video). Every person row shows where they came from.
 */
const fmtDate = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return String(d).slice(0, 10); }
};

export default function AdminTrainees() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isSuperAdmin = !!user?.is_super_admin;
  // Wipe tool is preview-only — never shown on the deployed app.
  const isPreviewEnv = window.location.hostname.endsWith("preview.emergentagent.com")
    || window.location.hostname.includes(".preview.emergentcf.cloud")
    || window.location.hostname === "localhost";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [tab, setTab] = useState("prospects"); // prospects | members | inactive | sponsors | video
  const [search, setSearch] = useState("");
  const [reject, setReject] = useState({ open: false, id: null, reason: "" });
  const [wipe, setWipe] = useState({ open: false, busy: false, confirmText: "" });
  const [videosOpen, setVideosOpen] = useState(false);
  const [sponsors, setSponsors] = useState([]);
  const [dleads, setDleads] = useState([]);
  const [leadFacets, setLeadFacets] = useState({ wallets: [], sponsors: [] });
  const [scopedWallet, setScopedWallet] = useState(null);
  const [myDiscover, setMyDiscover] = useState(null);
  const [syncedAt, setSyncedAt] = useState(null);
  const [fWallet, setFWallet] = useState("");
  const [fSponsor, setFSponsor] = useState("");
  const [veng, setVeng] = useState(null);
  const [pending, setPending] = useState([]);
  const [testHidden, setTestHidden] = useState(0);
  const [inactiveLogins, setInactiveLogins] = useState([]);
  const [memberAccounts, setMemberAccounts] = useState([]);
  const [journeyOpen, setJourneyOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [v2, sp, dl] = await Promise.all([
        api.get("/admin/onboarding-v2/all").then((r) => r.data.trainees || []).catch(() => []),
        api.get("/admin/onboarding-v2/sponsor-stats").then((r) => r.data.sponsors || []).catch(() => []),
        api.get("/admin/discover/leads", { params: { wallet: fWallet || undefined, sponsor: fSponsor || undefined } })
          .then((r) => r.data).catch(() => ({ leads: [] })),
      ]);
      setSponsors(sp);
      setDleads(dl.leads || []);
      setLeadFacets(dl.facets || { wallets: [], sponsors: [] });
      setScopedWallet(dl.scoped_wallet || null);
      api.get("/admin/video-engagement").then((r) => setVeng(r.data)).catch(() => {});
      api.get("/admin/pending-logins").then((r) => {
        setPending(r.data.pending || []);
        setTestHidden(r.data.test_accounts_hidden || 0);
      }).catch(() => {});
      api.get("/admin/inactive-logins").then((r) => setInactiveLogins(r.data.inactive || [])).catch(() => {});
      api.get("/admin/members").then((r) => setMemberAccounts(r.data || [])).catch(() => {});
      // A tracked link of our own — never hand out a bare /discover, it lands as "direct".
      api.get("/me/carypact-invite").then((r) => {
        const slug = r.data.referral_slug || "";
        if (!slug || !r.data.share_ready) return setMyDiscover(null);
        const sfx = r.data.share_suffix_needed && r.data.carypact_suffix ? `&s=${r.data.carypact_suffix}` : "";
        setMyDiscover(`${window.location.origin}/discover?ref=${slug}${sfx}`);
      }).catch(() => {});
      // Normalize into one shape (legacy Academy trainees retired).
      const norm = [
        ...v2.map(normalizeV2),
      ].sort((a, b) => (b.sort_at || "").localeCompare(a.sort_at || ""));
      setRows(norm);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load trainees");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (!loading) setSyncedAt(new Date()); }, [loading]);
  // Wallet / sponsor filters come from the server so the counts match.
  useEffect(() => { if (!loading) loadLeads(); }, [fWallet, fSponsor]);

  const loadLeads = async () => {
    try {
      const { data } = await api.get("/admin/discover/leads", {
        params: { wallet: fWallet || undefined, sponsor: fSponsor || undefined },
      });
      setDleads(data.leads || []);
      setLeadFacets(data.facets || { wallets: [], sponsors: [] });
      setScopedWallet(data.scoped_wallet || null);
    } catch { /* keep what we have */ }
  };

  const [purging, setPurging] = useState(false);
  const purgeTestLeads = async () => {
    setPurging(true);
    try {
      const { data } = await api.delete("/admin/discover/leads/test-rows");
      toast.success(`Cleared ${data.interested_leads} test lead${data.interested_leads === 1 ? "" : "s"}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not clear the test rows");
    } finally { setPurging(false); }
  };

  const setLeadWallet = async (lead, walletName) => {
    setDleads((prev) => prev.map((l) => (l.id === lead.id
      ? { ...l, sponsor_wallet_name: walletName || l.sponsor_wallet_name, sponsor_wallet_proven: true,
          sponsor_wallet_source: "set", sponsor_wallet_set_by: "you" }
      : l)));
    try {
      await api.patch(`/admin/discover/leads/${lead.id}/wallet`, { wallet_name: walletName || null });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save the wallet");
      load();
    }
  };

  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q ? rows.filter((r) => (r.email || "").toLowerCase().includes(q) || (r.name || "").toLowerCase().includes(q)) : rows;
    return {
      awaiting: filtered.filter((r) => r.bucket === "awaiting"),
      in_progress: filtered.filter((r) => r.bucket === "in_progress"),
      onboarded: filtered.filter((r) => r.bucket === "onboarded"),
      opted_out: filtered.filter((r) => r.bucket === "opted_out"),
      rejected: filtered.filter((r) => r.bucket === "rejected"),
    };
  }, [rows, search]);

  // Members are now created ONLY on the Wallets screen (attach the email to the
  // synced tile, then hit the "+"). From People we just take the admin straight
  // to that person's wallet so they can finish there — never make a member here.
  const openInWallets = (r) => {
    // For onboarding trainees we have their own wallet address; for discover
    // watchers we only have their email (their tile is matched by it). Never use
    // r.wallet here — on a prospect row that's the SPONSOR's wallet name.
    const q = (r.own_wallet || r.email || "").trim();
    navigate(`/wallets?q=${encodeURIComponent(q)}`);
  };

  const approvePending = async (row) => {
    setBusyId(row.id);
    try {
      await api.post(`/admin/pending-logins/${row.id}/approve`);
      toast.success(`${row.name || row.email} approved — they can sign in now`);
      setPending((prev) => prev.filter((p) => p.id !== row.id));
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error((d && typeof d === "object" ? d.message : d) || "Approve failed");
    } finally { setBusyId(null); }
  };

  const deactivateLogin = async (row) => {
    setBusyId(row.id);
    try {
      await api.delete(`/members/${row.id}`);
      toast.success(`${row.name || row.email} deactivated`);
      setPending((prev) => prev.filter((p) => p.id !== row.id));
      api.get("/admin/inactive-logins").then((r) => setInactiveLogins(r.data.inactive || [])).catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.detail || "Deactivate failed");
    } finally { setBusyId(null); }
  };

  const restoreLogin = async (row) => {
    setBusyId(row.id);
    try {
      await api.post(`/admin/members/${row.id}/restore`);
      toast.success(`${row.name || row.email} restored — back in "Needs you"`);
      setInactiveLogins((prev) => prev.filter((p) => p.id !== row.id));
      api.get("/admin/pending-logins").then((r) => setPending(r.data.pending || [])).catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.detail || "Restore failed");
    } finally { setBusyId(null); }
  };

  const openJourney = () => setJourneyOpen(true);

  const doWipe = async () => {
    if (wipe.confirmText.trim().toUpperCase() !== "WIPE") {
      toast.error("Type WIPE to confirm"); return;
    }
    setWipe({ ...wipe, busy: true });
    try {
      const { data } = await api.post("/admin/onboarding-v2/wipe-trainees?confirm=WIPE");
      toast.success(`Wiped ${data.leads_deleted} trainee record(s). Users & org chart untouched.`);
      setWipe({ open: false, busy: false, confirmText: "" });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Wipe failed");
      setWipe({ ...wipe, busy: false });
    }
  };

  const doReject = async () => {
    if (!reject.reason.trim()) { toast.error("Please give a reason"); return; }
    setBusyId(reject.id);
    try {
      await api.post(`/admin/onboarding-v2/${reject.id}/reject`, { reason: reject.reason.trim() });
      toast.success("Rejected. Trainee has been emailed.");
      setReject({ open: false, id: null, reason: "" });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reject failed");
    } finally { setBusyId(null); }
  };

  const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Copied"); };

  const q = search.trim().toLowerCase();
  const matchLogin = (p) => !q
    || (p.email || "").toLowerCase().includes(q)
    || (p.name || "").toLowerCase().includes(q)
    || (p.registration_wallet || "").toLowerCase().includes(q)
    || (p.sponsor_name || "").toLowerCase().includes(q);
  // Only surface a sign-in request once the person has their OWN CaryPact wallet
  // + link synced into our tree (can_approve). Under our rule nobody can be a
  // member without that, so a login with no synced wallet has nothing to request
  // yet — it stays hidden until their wallet appears in the tree.
  const pendingVisible = pending.filter(matchLogin).filter((p) => p.can_approve);
  // Signed up, no wallet attached to their name anywhere — so not a member, and
  // there is nothing to approve yet either. They live here until an admin puts
  // their email on a wallet in Wallets and clicks the person icon.
  const awaitingWallet = pending.filter(matchLogin).filter((p) => p.awaiting === "wallet");
  const inactiveLoginsVisible = inactiveLogins.filter(matchLogin);

  const dleadsVisible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? dleads.filter((l) => (l.email || "").toLowerCase().includes(q)
      || (l.name || "").toLowerCase().includes(q)
      || (l.phone || "").includes(q)
      || (l.sponsor_slug || "").toLowerCase().includes(q)
      || (l.sponsor_code || "").toLowerCase().includes(q)
      || (l.sponsor_wallet_name || "").toLowerCase().includes(q)
      || (l.sponsor_name || "").toLowerCase().includes(q)) : dleads;
  }, [dleads, search]);

  const watchingLeads = useMemo(() => dleadsVisible.filter((l) => !l.wallet_synced), [dleadsVisible]);

  // Anyone who is already a MEMBER (has their own live CaryPact wallet + account)
  // belongs in the Members tab, not the funnel — even if their old trainee record
  // still says "awaiting". Exclude them from Prospects by email.
  const memberEmails = useMemo(
    () => new Set(memberAccounts
      .filter((m) => m.has_live_wallet && !m.is_demo)
      .map((m) => (m.email || "").trim().toLowerCase())
      .filter(Boolean)),
    [memberAccounts]);

  // Everyone still in the funnel lives in ONE Prospects table: onboarding
  // trainees (in-progress AND submitted) plus discover watchers — minus anyone
  // who's already a member. Readiness to become a member is worked out per-row.
  const prospectsTrainees = useMemo(
    () => [...grouped.in_progress, ...grouped.awaiting]
      .filter((r) => !r.wallet_owner_self && !memberEmails.has((r.email || "").trim().toLowerCase())),
    [grouped.in_progress, grouped.awaiting, memberEmails]);
  const discoverProspects = useMemo(() => {
    // Match the table's own dedup: a discover watcher is hidden when their email
    // belongs to a trainee already in this table, or when they're already a member.
    const emails = new Set(prospectsTrainees.map((r) => (r.email || "").trim().toLowerCase()).filter(Boolean));
    return dleadsVisible.filter((l) => {
      const em = (l.email || "").trim().toLowerCase();
      return !emails.has(em) && !memberEmails.has(em);
    });
  }, [dleadsVisible, prospectsTrainees, memberEmails]);
  const prospectDiscover = discoverProspects;
  const prospectsCount = useMemo(() => {
    const known = new Set([...prospectsTrainees, ...discoverProspects]
      .map((r) => (r.email || "").toLowerCase()).filter(Boolean));
    const extra = awaitingWallet.filter((u) => !known.has((u.email || "").toLowerCase())).length;
    return prospectsTrainees.length + discoverProspects.length + extra;
  }, [prospectsTrainees, discoverProspects, awaitingWallet]);

  // Under our rule a "member" = someone with their OWN CaryPact wallet + link
  // (a synced tile in our tree = has_live_wallet). Admins are just promoted
  // members, so they count too. Accounts with no wallet aren't members yet.
  const walletMembers = useMemo(() => {
    const withWallet = memberAccounts.filter((m) => m.has_live_wallet && !m.is_demo);
    const query = search.trim().toLowerCase();
    return query
      ? withWallet.filter((m) => (m.email || "").toLowerCase().includes(query)
        || (m.name || "").toLowerCase().includes(query)
        || (m.node_label || "").toLowerCase().includes(query))
      : withWallet;
  }, [memberAccounts, search]);

  const peopleTabs = [
    { k: "prospects", label: "Prospects", icon: Megaphone, count: prospectsCount },
    { k: "members", label: "Members", icon: UserCheck, count: walletMembers.length },
    { k: "inactive", label: "Inactive", icon: XCircle, count: grouped.opted_out.length + grouped.rejected.length + inactiveLoginsVisible.length },
  ];
  const insightTabs = [
    { k: "sponsors", label: "Sponsors", icon: BarChart3, count: sponsors.length },
    { k: "video", label: "Video engagement", icon: Film, count: veng?.totals?.plays || 0 },
  ];

  const renderTabBtn = (t) => {
    const Ic = t.icon;
    const active = tab === t.k;
    return (
      <button key={t.k}
        data-testid={`tab-${t.k}`}
        onClick={() => setTab(t.k)}
        className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${active ? "border-indigo-600 text-indigo-700" : "border-transparent text-ink-3 hover:text-ink-1"}`}>
        <Ic size={14} /> {t.label}
        <span className={`inline-flex items-center justify-center min-w-[20px] px-1.5 h-5 text-xs rounded-full ${active ? "bg-indigo-100 text-indigo-700" : "bg-black/5 text-ink-3"}`}>{t.count}</span>
      </button>
    );
  };

  const traineeCardProps = { busyId, onOpenWallets: openInWallets, onReject: (id) => setReject({ open: true, id, reason: "" }), copy };

  // ---- Discover leads block (reused inside Prospects) ----
  const leadsBlock = (
    <>
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <p className="text-sm text-ink-3">
          {scopedWallet
            ? <>Leads that came through <strong>{scopedWallet}</strong> — switch the header back to All wallets to see everyone.</>
            : "People who opened the discover link — watch progress updates live as they view the videos."}
          {watchingLeads.some((l) => l.sponsor_wallet_name && !l.sponsor_wallet_proven) && (
            <span className="block text-[11px] mt-0.5" data-testid="wallet-assumed-legend">
              A wallet name with a <span className="inline-block w-[5px] h-[5px] rounded-full bg-amber-400/90 align-middle" /> was worked out rather than proven by the link — hover it to see how. Hover a row and use the arrow to correct it; your pick is final.
            </span>
          )}
        </p>
        <div className="flex items-center gap-2">
          <select value={fWallet} onChange={(e) => setFWallet(e.target.value)}
            data-testid="leads-wallet-filter" disabled={!!scopedWallet}
            title={scopedWallet ? `Scoped to ${scopedWallet} by the wallet switcher` : "Show only leads that came through one wallet"}
            className="h-8 px-2 rounded-md border border-black/15 bg-white text-[12px] max-w-[190px] disabled:opacity-60">
            <option value="">{scopedWallet ? `Wallet: ${scopedWallet}` : "All wallets"}</option>
            {(leadFacets.wallets || []).map((w) => (
              <option key={w.name} value={w.name}>{w.name} ({w.count})</option>
            ))}
          </select>
          <select value={fSponsor} onChange={(e) => setFSponsor(e.target.value)}
            data-testid="leads-sponsor-filter" title="Show only one sponsor's leads"
            className="h-8 px-2 rounded-md border border-black/15 bg-white text-[12px] max-w-[170px]">
            <option value="">All sponsors</option>
            {(leadFacets.sponsors || []).map((sp2) => (
              <option key={sp2.name} value={sp2.name}>{sp2.name} ({sp2.count})</option>
            ))}
          </select>
          {(fWallet || fSponsor) && (
            <Button variant="ghost" size="sm" onClick={() => { setFWallet(""); setFSponsor(""); }} data-testid="leads-filter-clear">
              <X size={13} className="mr-1" /> Clear
            </Button>
          )}
          {dleadsVisible.some((l) => l.is_test) && (
            <Button variant="outline" size="sm" onClick={purgeTestLeads} disabled={purging}
              data-testid="purge-test-leads-btn" title="Delete every …@test.botchaincp.com row — your real leads are untouched">
              {purging ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Trash2 size={13} className="mr-1.5" />}
              Clear test rows
            </Button>
          )}
          <Button variant="outline" size="sm" disabled={!myDiscover}
            onClick={() => myDiscover && copy(myDiscover)}
            data-testid="copy-discover-link-btn"
            title={myDiscover ? `Your tracked link — ${myDiscover}` : "Add your CaryPact invite link first (Wallets → Invite link) so clicks can be credited"}>
            <Copy size={13} className="mr-1.5" /> Copy my discover link
          </Button>
        </div>
      </div>
      <Card className="p-0 overflow-x-auto" data-testid="discover-leads-card">
        <table className="modern members-compact" data-testid="discover-leads-table">
          <thead>
            <tr>
              <th>Lead</th>
              <th>Phone</th>
              <th>Sizzle</th>
              <th>Presentation</th>
              <th>Status</th>
              <th title="All times shown in Eastern — hover a row for its full timeline">First seen · ET</th>
              <th>Sponsor</th>
              <th title="The wallet name of the sponsor whose link this visitor arrived with">Wallet name</th>
              <th title="Which campaign link, plain share link or the registration page brought this person in — hover for the tags">Came from</th>
            </tr>
          </thead>
          <tbody>
            {watchingLeads.length === 0 ? (
              <tr><td colSpan="9" className="text-center py-10 text-ink-3" data-testid="discover-leads-empty">No one is watching right now — share the /discover link to get some.</td></tr>
            ) : watchingLeads.map((l) => {
              const st = {
                watching_sizzle: ["Watching sizzle", "bg-black/5 text-ink-3"],
                info_submitted: ["Info submitted", "bg-blue-50 text-blue-700"],
                watching_present: ["Watching presentation", "bg-indigo-50 text-indigo-700"],
                watched_present: ["Watched presentation", "bg-amber-50 text-amber-700"],
                onboarding: ["Started onboarding", "bg-emerald-50 text-emerald-700"],
              }[l.status] || ["—", "bg-black/5 text-ink-3"];
              const sponsorLink = l.link_code
                ? `${window.location.origin}/discover?ref=${l.link_code}${l.link_suffix ? `&s=${l.link_suffix}` : ""}`
                : "";
              return (
                <tr key={l.id} className="group" data-testid={`discover-lead-row-${l.id}`}>
                  <td>
                    <div className="font-medium text-xs flex items-center gap-1.5 flex-wrap">
                      {l.is_test && (
                        <span className="inline-block px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-black/5 text-ink-3 align-middle"
                          data-testid={`discover-lead-test-${l.id}`} title="Throwaway tester — safe to clear">test</span>
                      )}
                      {l.invited_via === "email" && (
                        <span className="inline-block px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-blue-50 text-blue-700 align-middle"
                          data-testid={`discover-lead-emailed-${l.id}`} title="You emailed this person the link — identified from that invite">emailed</span>
                      )}
                      <span>{l.name || (l.email
                        ? <span className="font-mono text-[11px]">{l.email}</span>
                        : <span className="text-ink-3 italic">anonymous</span>)}</span>
                    </div>
                    {l.name && l.email && <div className="text-[11px] text-ink-3 font-mono">{l.email}</div>}
                  </td>
                  <td className="text-[11px] font-mono">{l.phone || "—"}</td>
                  {[["sizzle_pct", "sizzle"], ["present_pct", "present"]].map(([f, k]) => {
                    const p = Math.round(l[f] || 0);
                    return (
                      <td key={k} data-testid={`discover-${k}-${l.id}`}>
                        <div className="flex items-center gap-2 min-w-[90px]">
                          <div className="flex-1 h-1.5 rounded-full bg-black/5 overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${p}%`, background: p >= 95 ? "#00863A" : "#4060FF" }} />
                          </div>
                          <span className="text-[11px] font-mono num w-9 text-right">{p}%</span>
                        </div>
                      </td>
                    );
                  })}
                  <td><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${st[1]}`} data-testid={`discover-status-${l.id}`}>{st[0]}</span></td>
                  <td><SeenCell lead={l} /></td>
                  <td className="text-[11px]" data-testid={`discover-sponsor-${l.id}`}>
                    {l.sponsor_name
                      ? l.sponsor_name
                      : (l.sponsor_code || l.sponsor_slug)
                        ? <span className="text-amber-700" title={`This link code doesn't belong to any member (${l.sponsor_code || l.sponsor_slug})`}>unknown link</span>
                        : <span className="text-ink-3">direct</span>}
                  </td>
                  <td className="text-[11px]" data-testid={`discover-slug-${l.id}`}>
                    <WalletCell lead={l} onSet={setLeadWallet} />
                  </td>
                  <td className="text-[11px]" data-testid={`discover-origin-${l.id}`}>
                    <OriginChip origin={l.origin} testid={`origin-chip-${l.id}`} />
                    {l.link_code ? (
                      <button type="button" onClick={() => copy(sponsorLink)}
                        title={`Copy the full link they clicked — ${sponsorLink}`}
                        className="font-mono text-[10px] whitespace-nowrap inline-flex items-center gap-1 text-ink-3 hover:text-indigo-700 hover:underline mt-0.5">
                        {l.link_code}{l.link_suffix ? `·${l.link_suffix}` : ""} <Copy size={8} />
                      </button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
    </>
  );

  const sponsorsBlock = (
    <Card className="p-0 overflow-x-auto" data-testid="sponsor-stats-card">
      <table className="modern members-compact" data-testid="sponsor-stats-table">
        <thead>
          <tr>
            <th>Sponsor</th>
            <th>Funnel link</th>
            <th className="text-right">Signups</th>
            <th className="text-right">In training</th>
            <th className="text-right">Awaiting</th>
            <th className="text-right">Members</th>
            <th className="text-right">Opted out</th>
            <th className="text-right">Conversion</th>
          </tr>
        </thead>
        <tbody>
          {sponsors.length === 0 ? (
            <tr><td colSpan="8" className="text-center py-10 text-ink-3" data-testid="sponsors-empty">No funnel activity yet.</td></tr>
          ) : sponsors.map((s) => (
            <tr key={s.slug || "none"} data-testid={`sponsor-row-${s.slug || "none"}`}>
              <td>
                <div className="font-medium text-xs">{s.sponsor_name}</div>
                {s.sponsor_email && <div className="text-[11px] text-ink-3 font-mono">{s.sponsor_email}</div>}
              </td>
              <td>
                {s.slug ? (
                  <button onClick={() => copy(`${window.location.origin}/join?ref=${s.slug}`)}
                          className="inline-flex items-center gap-1 text-[11px] font-mono text-indigo-700 hover:underline"
                          data-testid={`sponsor-link-${s.slug}`}>
                    /join?ref={s.slug} <Copy size={9} />
                  </button>
                ) : <span className="text-ink-3 text-[11px]">—</span>}
              </td>
              <td className="text-right font-semibold num">{s.signups}</td>
              <td className="text-right num">{s.in_progress}</td>
              <td className="text-right num">{s.awaiting}</td>
              <td className="text-right num text-emerald-700 font-semibold">{s.members}</td>
              <td className="text-right num text-ink-3">{s.opted_out}</td>
              <td className="text-right">
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${s.conversion >= 50 ? "bg-emerald-50 text-emerald-700" : s.conversion > 0 ? "bg-amber-50 text-amber-700" : "bg-black/5 text-ink-3"}`}>
                  {s.conversion}%
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );

  const inactiveCards = [...grouped.rejected, ...grouped.opted_out];
  const inactiveEmpty = inactiveCards.length === 0 && inactiveLoginsVisible.length === 0;

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">People</h1>
            <p className="text-ink-3 mt-1 text-sm">
              Everyone in your funnel, one table — click a row for full progress. A member is only ever made in Wallets.
              {syncedAt && (
                <span className="text-ink-3" data-testid="trainees-synced-at"> · synced {etTime(syncedAt)} ET</span>
              )}
            </p>
          </div>
          <div className="flex gap-2">
            <Input placeholder="Search name or email…" value={search} onChange={(e) => setSearch(e.target.value)} className="w-64" data-testid="trainee-search" />
            <Button variant="outline" onClick={load} disabled={loading} data-testid="refresh-btn">
              {loading ? <Loader2 className="animate-spin" size={14} /> : <RefreshCw size={14} />}
            </Button>
            {isSuperAdmin && (
              <Button variant="outline" onClick={openJourney} data-testid="journey-map-btn"
                style={{ background: "rgba(64,96,255,0.12)", borderColor: "rgba(64,96,255,0.4)", color: "#4060FF" }}
                title="See the whole member journey on one page as a flow diagram">
                <FileText size={14} className="mr-1" /> Journey map
              </Button>
            )}
            {isSuperAdmin && (
              <Button variant="outline" onClick={() => setVideosOpen(true)} data-testid="training-videos-btn">
                <Film size={14} className="mr-1" /> Training videos
              </Button>
            )}
            {isSuperAdmin && isPreviewEnv && (
              <Button variant="outline" onClick={() => setWipe({ open: true, busy: false, confirmText: "" })} data-testid="wipe-trainees-btn"
                      className="border-rose-300 text-rose-700 hover:bg-rose-50">
                <Trash2 size={14} className="mr-1" /> Wipe trainee data
              </Button>
            )}
          </div>
        </div>

        {/* Tabs — people queues, then a divider, then Insights */}
        <div className="flex gap-1 mb-4 border-b border-black/10 flex-wrap items-center">
          {peopleTabs.map(renderTabBtn)}
          <span className="mx-2 h-5 w-px bg-black/10 self-center" />
          <span className="text-[10px] font-bold uppercase tracking-wider text-ink-3 px-1 self-center">Insights</span>
          {insightTabs.map(renderTabBtn)}
        </div>

        {loading ? (
          <div className="text-center py-16"><Loader2 className="animate-spin mx-auto" /></div>
        ) : tab === "prospects" ? (
          <div className="space-y-6" data-testid="prospects-view">
            {/* Sign-in requests — people asking to log in (separate from membership). */}
            {pendingVisible.length > 0 && (
              <section data-testid="signin-requests-list">
                <SectionHead icon={Zap} title="Sign-in requests" count={pendingVisible.length}
                  sub="People whose own CaryPact wallet has synced into your tree and who are asking to sign in. Approving lets them log in — you still make them a member over in Wallets." />
                <div className="space-y-3">
                  {pendingVisible.map((p) => (
                    <LoginCard key={`login-${p.id}`} p={p} tag="Approve sign-in" mode="needs"
                      busyId={busyId} onApprove={approvePending} onDeactivate={deactivateLogin} />
                  ))}
                </div>
              </section>
            )}

            {/* The one funnel table — everyone in it. Anyone who's finished and
                whose wallet has synced shows a green "Make member" action at the end. */}
            <section>
              {dleadsVisible.some((l) => l.is_test) && (
                <div className="flex justify-end mb-2">
                  <Button variant="outline" size="sm" onClick={purgeTestLeads} disabled={purging}
                    data-testid="purge-test-leads-btn" title="Delete every …@test.botchaincp.com row — your real leads are untouched">
                    {purging ? <Loader2 className="animate-spin" size={12} /> : <><Trash2 size={12} className="mr-1" /> Clear test rows</>}
                  </Button>
                </div>
              )}
              <ProspectsPanel inProgress={prospectsTrainees} watching={prospectDiscover}
                allDiscover={dleadsVisible} registered={awaitingWallet} copy={copy}
                onMakeMember={openInWallets} onReject={deactivateLogin} busyId={busyId} />
              {testHidden > 0 && (
                <div className="mt-2 text-[11px] text-ink-3" data-testid="test-accounts-hidden">
                  {testHidden} test {testHidden === 1 ? "account is" : "accounts are"} hidden
                  (left behind by automated checks — not real sign-ups).
                </div>
              )}
            </section>
          </div>
        ) : tab === "members" ? (
          walletMembers.length === 0 ? (
            <NothingHere text="No members with their own CaryPact wallet yet." />
          ) : (
            <div className="space-y-3" data-testid="members-list">
              <p className="text-xs text-ink-3 -mt-1 mb-1" data-testid="members-definition-note">
                Everyone with their own CaryPact wallet + link. Admins are just promoted members. A login with no wallet isn&apos;t a member at all — it stays in Prospects, marked &ldquo;Registered · no wallet&rdquo; in the Account column, until you attach a wallet in Wallets and click the person icon.
              </p>
              <MembersPanel members={walletMembers} copy={copy} />
            </div>
          )
        ) : tab === "inactive" ? (
          inactiveEmpty ? (
            <NothingHere text="No opted-out, rejected or deactivated people." />
          ) : (
            <div className="space-y-8" data-testid="inactive-view">
              {inactiveCards.length > 0 && (
                <section>
                  <SectionHead icon={XCircle} title="Opted out & rejected" count={inactiveCards.length}
                    sub="Trainees who backed out or whose submission you sent back." />
                  <div className="space-y-3">
                    {inactiveCards.map((r) => <TraineeCard key={`${r.flow}-${r.id}`} r={r} {...traineeCardProps} />)}
                  </div>
                </section>
              )}
              {inactiveLoginsVisible.length > 0 && (
                <section>
                  <SectionHead icon={KeyRound} title="Deactivated logins" count={inactiveLoginsVisible.length}
                    sub="Self-registered accounts you turned off — kept as a record. Restore to send them back to Needs you." />
                  <div className="space-y-3">
                    {inactiveLoginsVisible.map((p) => (
                      <LoginCard key={`inactive-${p.id}`} p={p} mode="inactive" busyId={busyId} onRestore={restoreLogin} />
                    ))}
                  </div>
                </section>
              )}
            </div>
          )
        ) : tab === "sponsors" ? (
          sponsorsBlock
        ) : tab === "video" ? (
          <VideoEngagement veng={veng} search={search} copy={copy} />
        ) : null}
      </div>

      {journeyOpen && <JourneyMap onClose={() => setJourneyOpen(false)} />}

      <Dialog open={reject.open} onOpenChange={(open) => setReject({ ...reject, open })}>
        <DialogContent className="bg-white text-ink-1">
          <DialogHeader>
            <DialogTitle>Ask the trainee to fix something</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-ink-3">They&apos;ll get an email with this exact message and can resubmit.</p>
          <Textarea rows={4} value={reject.reason} onChange={(e) => setReject({ ...reject, reason: e.target.value })}
                    placeholder="e.g. The CaryPact link doesn't include your wallet address — please redo Section A step 3." data-testid="reject-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setReject({ open: false, id: null, reason: "" })}>Cancel</Button>
            <Button variant="destructive" onClick={doReject} data-testid="reject-confirm">Send rejection</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Wipe trainee data — one-shot, admin-guarded */}
      <Dialog open={wipe.open} onOpenChange={(open) => setWipe({ ...wipe, open })}>
        <DialogContent data-testid="wipe-modal" className="bg-white text-ink-1">
          <DialogHeader>
            <DialogTitle className="text-rose-700">Wipe all trainee data?</DialogTitle>
            <DialogDescription>
              This deletes every row in the leads + academy_captures collections so you can rerun onboarding end-to-end with a fresh trainee.
              Existing <b>users, org chart tiles, wallets, and revenue</b> are left untouched.
            </DialogDescription>
          </DialogHeader>
          <div>
            <label className="text-xs text-ink-3">Type <code className="px-1 rounded bg-black/5">WIPE</code> to confirm</label>
            <Input value={wipe.confirmText} onChange={(e) => setWipe({ ...wipe, confirmText: e.target.value })} placeholder="WIPE" data-testid="wipe-confirm-input" className="mt-1" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWipe({ open: false, busy: false, confirmText: "" })} disabled={wipe.busy}>Cancel</Button>
            <Button variant="destructive" onClick={doWipe} disabled={wipe.busy || wipe.confirmText.trim().toUpperCase() !== "WIPE"} data-testid="wipe-confirm-btn">
              {wipe.busy ? <Loader2 className="animate-spin" size={14} /> : <><Trash2 size={12} className="mr-1" /> Wipe now</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <TrainingVideosModal open={videosOpen} onOpenChange={setVideosOpen} />
    </div>
  );
}

function NothingHere({ text }) {
  return (
    <Card className="p-10 text-center">
      <div className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto mb-3"><CheckCircle2 size={24} /></div>
      <h3 className="text-lg font-semibold">Nothing here</h3>
      <p className="text-ink-3 text-sm mt-1">{text}</p>
    </Card>
  );
}

function SectionHead({ icon: Ic, title, count, sub }) {
  return (
    <div className="mb-3">
      <div className="flex items-center gap-2">
        {Ic && <Ic size={15} className="text-ink-3" />}
        <h2 className="text-sm font-bold uppercase tracking-wider text-ink-2">{title}</h2>
        <span className="inline-flex items-center justify-center min-w-[20px] px-1.5 h-5 text-xs rounded-full bg-black/5 text-ink-3">{count}</span>
      </div>
      {sub && <p className="text-xs text-ink-3 mt-0.5">{sub}</p>}
    </div>
  );
}

function FlowPill({ flow }) {
  const label = flow === "onboarding_v2" ? "CaryPact" : "Academy";
  const cls = flow === "onboarding_v2" ? "bg-indigo-100 text-indigo-700" : "bg-amber-100 text-amber-800";
  return <span className={`inline-block mt-1.5 px-1.5 py-0.5 rounded text-[10px] font-semibold ${cls}`}>{label}</span>;
}

// Where a login-page sign-up was credited. These never carry a personal referral
// link — they're assigned by link rotation (or the admin fallback), so we say so.
function LoginCameFrom({ cf }) {
  const kind = cf?.kind;
  if (!cf || kind === "direct" || (!cf.sponsor_name && !cf.code && !cf.wallet)) {
    return <span className="text-amber-700" data-testid="came-from-direct">Login page · direct — no referral link</span>;
  }
  return (
    <span data-testid="came-from-login">
      <span className="font-medium">Login page</span>
      {cf.sponsor_name ? <> · credited to <span className="font-medium">{cf.sponsor_name}</span></> : null}
      {cf.wallet ? <span className="text-ink-3"> · {cf.wallet}</span> : null}
      {cf.code ? <span className="text-ink-3 font-mono"> · /{cf.code}</span> : null}
      {kind === "rotation" ? <span className="text-ink-3"> (via link rotation)</span>
        : kind === "fallback" ? <span className="text-ink-3"> (no rotation set)</span> : null}
    </span>
  );
}

function LoginCard({ p, tag, mode, busyId, onApprove, onDeactivate, onRestore }) {
  return (
    <Card className="p-4" data-testid={`login-row-${p.id}`}>
      <div className="grid md:grid-cols-12 gap-4 items-center">
        <div className="md:col-span-5">
          {mode === "needs" && p.can_approve
            ? <span className="inline-flex items-center gap-1 mb-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800"
                data-testid={`tag-ready-${p.id}`}><CheckCircle2 size={10} /> Ready to approve</span>
            : tag && (
              <span className="inline-block mb-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800"
                data-testid={`tag-login-${p.id}`}>{tag}</span>
          )}
          <div className="font-semibold text-slate-900">{p.name || "—"}</div>
          <div className="text-xs text-ink-3 font-mono">{p.email}</div>
          {p.phone && <div className="text-xs text-ink-3">{p.phone}</div>}
        </div>
        <div className="md:col-span-4">
          <div className="text-xs uppercase tracking-widest text-ink-3 mb-1">Came from</div>
          <div className="text-xs" data-testid={`login-came-from-${p.id}`}><LoginCameFrom cf={p.came_from} /></div>
          <div className="text-[11px] text-ink-3 mt-1 flex items-center gap-1"><Clock size={11} /> Registered {fmtDate(p.created_at)}</div>
          {mode === "inactive" && p.archived_by && (
            <div className="text-[11px] text-ink-3 mt-0.5">Deactivated by {p.archived_by}{p.archived_at ? ` · ${fmtDate(p.archived_at)}` : ""}</div>
          )}
        </div>
        <div className="md:col-span-3 flex flex-col gap-1.5">
          {mode === "needs" ? (
            <>
              <Button size="sm" data-testid={`login-approve-${p.id}`}
                disabled={busyId === p.id || p.can_approve === false}
                title={p.can_approve === false ? (p.blocked_reason || "Their CaryPact wallet isn't synced yet") : "Approve this sign-in"}
                onClick={() => onApprove(p)}>
                {busyId === p.id ? <Loader2 className="animate-spin" size={12} /> : <><CheckCircle2 size={12} className="mr-1" /> Approve sign-in</>}
              </Button>
              {p.can_approve === false && (
                <span className="text-[11px] text-amber-700 leading-tight inline-flex items-start gap-1" data-testid={`login-blocked-${p.id}`}>
                  <Wallet size={11} className="mt-0.5 shrink-0" /> {p.blocked_reason || "No CaryPact wallet synced yet"}
                </span>
              )}
              <Button size="sm" variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50"
                data-testid={`login-deactivate-${p.id}`} disabled={busyId === p.id} onClick={() => onDeactivate(p)}>
                <XCircle size={12} className="mr-1" /> Not ours — deactivate
              </Button>
            </>
          ) : (
            <Button size="sm" variant="outline" data-testid={`login-restore-${p.id}`} disabled={busyId === p.id} onClick={() => onRestore(p)}>
              {busyId === p.id ? <Loader2 className="animate-spin" size={12} /> : <><RotateCcw size={12} className="mr-1" /> Restore</>}
            </Button>
          )}
          {p.email && (
            <Link to={`/admin/trace?email=${encodeURIComponent(p.email)}`} data-testid={`trace-link-${p.id}`}
              className="text-[11px] text-ink-3 hover:text-ink-1 inline-flex items-center gap-1 self-start mt-0.5">
              <Fingerprint size={11} /> Trace journey
            </Link>
          )}
        </div>
      </div>
    </Card>
  );
}

function TraineeCard({ r, tag, busyId, onOpenWallets, onReject, copy }) {
  return (
    <Card className="p-4" data-testid={`row-${r.id}`}>
      <div className="grid md:grid-cols-12 gap-4 items-center">
        {/* Person + where they came from */}
        <div className="md:col-span-3">
          {tag && (
            <span className="inline-block mb-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-indigo-100 text-indigo-700"
              data-testid={`tag-trainee-${r.id}`}>{tag}</span>
          )}
          <div className="font-semibold text-slate-900">{r.name || "—"}</div>
          <div className="text-xs text-ink-3">{r.email}</div>
          <div className="text-xs text-ink-3 mt-1" data-testid={`came-from-${r.id}`}>
            {r.location ? <span>{r.location} · </span> : null}
            Invited by:{" "}
            {r.sponsor_name ? (
              <>
                <span className="font-medium">{r.sponsor_name}</span>
                {r.sponsor_email ? <span className="text-ink-3"> ({r.sponsor_email})</span> : null}
                {r.sponsor ? <span className="text-ink-3 font-mono"> · /{r.sponsor}</span> : null}
              </>
            ) : r.sponsor ? (
              <span className="font-mono">/{r.sponsor} <span className="text-amber-700">· unknown</span></span>
            ) : (
              <span className="text-amber-700">no funnel link — direct signup</span>
            )}
          </div>
          <div className="mt-1.5">
            <OriginChip origin={r.origin} testid={`trainee-origin-${r.id}`} />
          </div>
        </div>

        {/* Progress */}
        <div className="md:col-span-3">
          <div className="text-xs uppercase tracking-widest text-ink-3 mb-1">Progress</div>
          <div className="text-sm font-mono" data-testid={`progress-${r.id}`}>{r.progress_label}</div>
          <FlowPill flow={r.flow} />
          <div className="text-[11px] text-ink-3 mt-1.5 flex items-center gap-1" data-testid={`started-${r.id}`}>
            <Clock size={11} />
            Started {fmtDate(r.started_at)}
            {r.last_activity_at ? <span className="text-ink-3"> · last active {fmtDate(r.last_activity_at)}</span> : null}
          </div>
        </div>

        {/* Wallet / referrer */}
        <div className="md:col-span-3">
          {r.flow === "onboarding_v2" ? (
            <>
              <div className="text-xs uppercase tracking-widest text-ink-3 mb-1">Submitted link</div>
              {r.carypact_url ? (
                <a href={r.carypact_url} target="_blank" rel="noopener noreferrer"
                   className="text-blue-600 hover:underline text-xs break-all inline-flex items-start gap-1">
                  <span>{r.carypact_url.length > 45 ? r.carypact_url.slice(0, 45) + "…" : r.carypact_url}</span>
                  <ExternalLink size={10} className="mt-0.5 flex-shrink-0" />
                </a>
              ) : <span className="text-xs text-ink-3">not submitted yet</span>}
              <div className="mt-1.5 space-y-0.5 text-xs">
                {r.wallet ? (
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <button onClick={() => copy(r.wallet)} className="font-mono text-emerald-700 hover:underline inline-flex items-center gap-1" data-testid={`wallet-${r.id}`}>
                      <Wallet size={11} />{r.wallet.slice(0, 10)}…{r.wallet.slice(-6)} <Copy size={9} />
                    </button>
                    {r.wallet_via_suffix && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200" title="Their invite link's suffix matched this wallet in the synced tree">matched via link suffix</span>
                    )}
                  </div>
                ) : r.carypact_url ? <span className="text-amber-700">no 0x wallet in link</span> : null}
                {r.ref ? <div className="text-ink-3">Ref: <span className="font-mono">{r.ref}</span></div> : null}
              </div>
            </>
          ) : (
            <div className="text-xs text-ink-3">Old 12-lesson flow — no CaryPact link required</div>
          )}
        </div>

        {/* Actions / status */}
        <div className="md:col-span-3 flex flex-col gap-1.5">
          {r.bucket === "awaiting" ? (
            <>
              {r.flow === "onboarding_v2" && r.blocked_reason ? (
                <div className="text-xs rounded-md p-2 bg-amber-50 text-amber-900 border border-amber-200" data-testid={`blocked-${r.id}`}>
                  <div className="font-semibold mb-0.5">Can&apos;t approve yet</div>
                  {r.blocked_reason === "no_wallet" && (
                    <>Their link&apos;s suffix doesn&apos;t match any wallet in the synced tree yet. Run a CaryPact sync from Org Chart, then refresh — or reject and ask them to paste their own invite link.</>
                  )}
                  {r.blocked_reason === "sponsor_link" && (
                    <>They pasted their <b>sponsor&apos;s</b> invite link, not their own. Reject with a note asking them to paste <b>their own</b> CaryPact invite link.</>
                  )}
                  {r.blocked_reason === "wallet_already_linked" && (
                    <>The wallet in this link is already linked to another member&apos;s login.</>
                  )}
                  {r.blocked_reason === "ambiguous_suffix" && (
                    <>Multiple wallets match this link&apos;s suffix — link them manually from the Org Chart.</>
                  )}
                  {r.blocked_reason === "wallet_not_synced" && (
                    <>Wallet isn&apos;t in the synced tree yet. Run a CaryPact sync from Org Chart, then refresh here.</>
                  )}
                </div>
              ) : null}
              <Button size="sm" data-testid={`open-wallets-${r.id}`}
                      onClick={() => onOpenWallets(r)}
                      title="Open this person's wallet in the Wallets screen — confirm the email there, then hit + to make them a member">
                <Wallet size={12} className="mr-1" /> Open in Wallets
              </Button>
              {r.flow === "onboarding_v2" && (
                <Button size="sm" variant="outline" data-testid={`reject-${r.id}`} disabled={busyId === r.id} onClick={() => onReject(r.id)}>
                  <X size={12} className="mr-1" /> Reject
                </Button>
              )}
            </>
          ) : (
            <div className="text-xs text-ink-3" data-testid={`status-${r.id}`}>{r.status_label || "—"}</div>
          )}
        </div>
      </div>
    </Card>
  );
}

function normalizeV2(t) {
  const p = t.onboarding_v2_progress || {};
  const parsed = t.onboarding_v2_parsed || {};
  const a = (p.section_a_completed || []).length;
  const b = (p.section_b_completed || []).length;
  const pre = Math.min(p.pre_step_index || 0, 6);
  const submitted = !!t.onboarding_v2_submitted_at;
  let bucket = "in_progress";
  let status_label = "In progress";
  if (t.onboarding_v2_opted_out) { bucket = "opted_out"; status_label = "Opted out"; }
  else if (t.status === "onboarded") { bucket = "onboarded"; status_label = "Onboarded"; }
  else if (t.status === "onboarding_rejected") { bucket = "rejected"; status_label = "Rejected — awaiting their resubmit"; }
  else if (t.status === "onboarding_pending_approval") { bucket = "awaiting"; status_label = "Awaiting approval"; }
  return {
    id: t.id,
    flow: "onboarding_v2",
    email: t.email,
    name: t.name,
    sponsor: t.sponsor_slug,
    sponsor_name: t._sponsor_name,
    sponsor_email: t._sponsor_email,
    origin: t.origin,
    location: [p.country, p.state].filter(Boolean).join(", "),
    progress_label: `Pre ${pre}/6 · A ${a}/4 · B ${b}/3`,
    carypact_url: t.onboarding_v2_carypact_url,
    wallet: parsed.wallet_address,
    wallet_via_suffix: !!t._wallet_via_suffix,
    ref: parsed.referrer_code,
    submitted, bucket, status_label,
    user_id: t.user_id,
    can_approve: t._can_approve,
    blocked_reason: t._blocked_reason,
    wallet_in_tree: t._wallet_in_tree,
    wallet_taken: t._wallet_taken,
    wallet_owner_self: t._wallet_owner_self,
    phone: t.phone,
    created_at: t.created_at,
    onboarded_via: t.onboarded_via,
    pre_step_index: Math.min(p.pre_step_index || 0, 5),
    section_a: p.section_a_completed || [],
    section_b: p.section_b_completed || [],
    started_at: t.created_at,
    last_activity_at: p.last_activity_at || t.onboarding_v2_submitted_at,
    sort_at: t.onboarding_v2_submitted_at || p.last_activity_at || t.created_at,
  };
}

function normalizeOld(t) {
  const done = (t.academy_progress || []).length;
  const captures = t.captures || {};
  const capsDone = ["carypact_link", "wallet_address", "country_state", "name_email"]
    .filter((k) => captures[k] || (k === "country_state" && captures.country && captures.state) || (k === "name_email" && t.name && t.email)).length;
  const graduated = done >= 12 && capsDone >= 4;
  const alreadyOnboarded = t.status === "onboarded";
  let bucket = "in_progress";
  let status_label = `Lesson ${done}/12`;
  if (alreadyOnboarded) { bucket = "onboarded"; status_label = "Onboarded"; }
  else if (graduated) { bucket = "awaiting"; status_label = "Ready to provision"; }
  return {
    id: t.id,
    flow: "academy_v1",
    email: t.email,
    name: t.name,
    sponsor: t.referred_by || t.referral_slug,
    location: [captures.country, captures.state].filter(Boolean).join(", "),
    progress_label: `Lessons ${done}/12 · Captures ${capsDone}/4`,
    carypact_url: null, wallet: null, ref: null,
    submitted: graduated,
    bucket, status_label,
    user_id: t.user_id,
    phone: t.phone,
    created_at: t.academy_enrolled_at || t.created_at,
    academy_done: done, academy_total: 12,
    pre_step_index: 0, section_a: [], section_b: [],
    started_at: t.academy_enrolled_at || t.created_at,
    last_activity_at: null,
    sort_at: t.academy_enrolled_at || t.created_at,
  };
}
